import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Save, X, Settings } from "lucide-react";

export default function IDGSavedActionsTab() {
  const [actions, setActions] = useState([]);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Form states
  const [editingAction, setEditingAction] = useState(null); // action object or null
  const [form, setForm] = useState({ name: "", reply_content: "", lock_topic: false, move_to_board_id: "" });
  const [showForm, setShowForm] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [actionsRes, boardsRes] = await Promise.all([
        api.get("/idg/admin/saved-actions"),
        api.get("/boards")
      ]);
      setActions(actionsRes.data || []);
      setBoards(boardsRes.data || []);
    } catch (e) {
      toast.error("Eroare la încărcarea datelor: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleEdit = (act) => {
    setEditingAction(act);
    setForm({
      name: act.name,
      reply_content: act.reply_content || "",
      lock_topic: act.lock_topic || false,
      move_to_board_id: act.move_to_board_id || ""
    });
    setShowForm(true);
  };

  const handleCreateNew = () => {
    setEditingAction(null);
    setForm({ name: "", reply_content: "", lock_topic: false, move_to_board_id: "" });
    setShowForm(true);
  };

  const handleDelete = async (actionId) => {
    if (!window.confirm("Sigur dorești să ștergi această acțiune salvată?")) return;
    try {
      await api.delete(`/idg/admin/saved-actions/${actionId}`);
      toast.success("Acțiune ștearsă!");
      loadData();
    } catch (e) {
      toast.error("Ștergere eșuată: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Numele este obligatoriu");
    
    try {
      if (editingAction) {
        await api.patch(`/idg/admin/saved-actions/${editingAction.id}`, form);
        toast.success("Acțiune salvată actualizată!");
      } else {
        await api.post("/idg/admin/saved-actions", form);
        toast.success("Acțiune salvată adăugată!");
      }
      setShowForm(false);
      loadData();
    } catch (e) {
      toast.error("Eroare la salvare: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="space-y-4" data-testid="saved-actions-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="tf-heading text-2xl font-bold uppercase tracking-wider flex items-center gap-2">
            <Settings className="w-6 h-6 text-[var(--tf-primary)]" /> Macro-uri Moderare (Saved Actions)
          </h2>
          <p className="text-xs text-zinc-500 tf-mono">Configurare macro-uri de moderare automată (răspunsuri predefinite, mutare, închidere)</p>
        </div>
        {!showForm && (
          <button onClick={handleCreateNew} className="tf-btn-primary tf-clip-btn text-xs flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Adaugă Macro
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="tf-surface tf-clip-card p-5 space-y-4 border border-white/5">
          <div className="flex items-center justify-between border-b border-white/5 pb-2">
            <h3 className="text-sm font-bold text-white uppercase">{editingAction ? "Editează Macro" : "Adaugă Macro Nou"}</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="tf-section-title block mb-1">Nume Macro (ex: Soluționat & Închis)</label>
              <input 
                type="text" 
                value={form.name} 
                onChange={(e) => setForm({ ...form, name: e.target.value })} 
                required 
                className="tf-input"
                placeholder="Numele sugestiv al acțiunii"
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="tf-section-title block mb-1">Conținut răspuns predefinit (opțional)</label>
              <textarea 
                value={form.reply_content} 
                onChange={(e) => setForm({ ...form, reply_content: e.target.value })} 
                rows={3} 
                className="tf-input"
                placeholder="Răspunsul care se va adăuga în topic automat la rulare..."
              />
            </div>

            <div>
              <label className="tf-section-title block mb-1">Mută în board-ul (opțional)</label>
              <select 
                value={form.move_to_board_id} 
                onChange={(e) => setForm({ ...form, move_to_board_id: e.target.value })} 
                className="tf-input"
              >
                <option value="">Nu schimba board-ul</option>
                {boards.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2 pt-6">
              <input 
                type="checkbox" 
                id="lock_topic" 
                checked={form.lock_topic} 
                onChange={(e) => setForm({ ...form, lock_topic: e.target.checked })} 
                className="w-4 h-4 accent-[var(--tf-primary)] bg-zinc-900 border-white/10 rounded"
              />
              <label htmlFor="lock_topic" className="text-sm font-bold text-zinc-300 select-none cursor-pointer">Închide topicul (Lock)</label>
            </div>
          </div>

          <div className="flex gap-2 pt-2 border-t border-white/5">
            <button type="submit" className="tf-btn-primary tf-clip-btn text-xs flex items-center gap-1">
              <Save className="w-3.5 h-3.5" /> Salvează
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="tf-btn-secondary tf-clip-btn text-xs">Anulează</button>
          </div>
        </form>
      )}

      <div className="tf-surface tf-clip-card overflow-hidden">
        <table className="w-full text-left text-sm text-zinc-300 leading-normal">
          <thead>
            <tr className="bg-white/5 text-zinc-400 text-xs font-bold uppercase tracking-wider border-b border-white/5">
              <th className="px-5 py-3">Nume Macro</th>
              <th className="px-5 py-3">Răspuns adăugat</th>
              <th className="px-5 py-3">Mută în</th>
              <th className="px-5 py-3">Închide topic</th>
              <th className="px-5 py-3 text-right">Acțiuni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {actions.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-10 text-center text-zinc-500 italic">Nu există macro-uri create.</td>
              </tr>
            ) : (
              actions.map((act) => {
                const targetBoard = boards.find(b => b.id === act.move_to_board_id);
                return (
                  <tr key={act.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-5 py-3 font-bold text-white">{act.name}</td>
                    <td className="px-5 py-3 max-w-xs truncate">{act.reply_content || <span className="text-zinc-600 italic">fără răspuns</span>}</td>
                    <td className="px-5 py-3">{targetBoard ? targetBoard.name : <span className="text-zinc-600 italic">—</span>}</td>
                    <td className="px-5 py-3 font-mono text-xs">{act.lock_topic ? <span className="text-red-400 font-bold">DA</span> : "NU"}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex justify-end gap-1.5">
                        <button onClick={() => handleEdit(act)} className="p-1 hover:text-orange-500" title="Editează"><Edit2 className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(act.id)} className="p-1 hover:text-red-500" title="Șterge"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
